import React, { useState, useContext } from 'react'
import PropTypes from 'prop-types'
import { useParams } from 'react-router-dom'
import { defaultSiteProposalValidationResults } from 'constants/defaultSiteProposalValidationResults'
import Select from 'components/Select'
import RemoveIcon from 'assets/icons/remove-icon'
import SpeciesParameterSiteSpeciesDataContent from './speciesParameterSiteSpeciesDataContent'
import SpeciesParameterHeader from './speciesParameterHeader'
import SpeciesParameterGlobalDataContent from './speciesParameterGlobalDataContent'
import RegionalNationalEstimatesDataContent from './regionalNationalEstimatesDataContent'
import Accordion from 'components/Accordion'
import { requestCreateSiteSpecieAssesmentParameter } from 'api/siteSpecieAssesmentParameter'
import { specieAssessmentParameterType, NUMBER_OF_MATURE_INDIVIDUALS_ID } from 'constants/speciesParameterType'
import capitalizeFirstLetterString from 'utils/capitalizeFirstLetter'
import { SITE_STATUS_DRAFT_ID, SITE_STATUS_NOMINATED_ID, SITE_STATUS_PROPOSED_ID, SITE_STATUS_REVIEWED_BY_RFP_ID } from 'constants/siteStatuses'
import { arrIdCriteria, getIdCriteria, D1b_Value, D1a_Value, D3_Value, B2_Value, D2_Value } from './constants'
import { canViewSensitiveSpeciesAtSiteFullAccess } from 'utils/rolePermissions'
import { seasonalDistribution } from 'constants/seasonalDistribution'
import capitalize from 'utils/capitalize'
import { parameterOptions, congregatoryGroupOptions, SEASON_RESIDENT_ID } from 'constants/parametersOptions'
import PulsingNotification from 'features/PulsingNotification'
import { SiteContext } from 'providers/site'
import { ProfileContext } from 'providers/profile'
import { useIBAAccess } from 'hooks/useIBAAccess'
import toNumberOrNull from 'utils/toNumberOrNull'
import {
  SpeciesDetailsContainer,
  SpeciesDetailsItemWrapper,
  SpeciesDetailsParameterIdContainer, SpeciesDetailsParameterIdWrapper,
  SpeciesDetailsRemoveParameter,
  SpeciesValidationIconWrapper, SpeciesValidationIcon,
  SpeciesAssessmentStyleWrapper, SpeciesAcordionWrapper
} from '../style'

const defaultParameterOptions = {
  parameterId: specieAssessmentParameterType.map((item) => {
    return {
      label: `${item.label} ${capitalizeFirstLetterString(item.name)}`,
      value: item.id
    }
  }),
  SeasonalDistributionId: seasonalDistribution.map((element) => {
    return {
      value: element.id,
      label: capitalize(element.name)
    }
  })
}

const defaultOptions = parameterOptions.map((item) => {
  return {
    value: item.id,
    label: `${item.label} ${capitalizeFirstLetterString(item.name)}`,
    seasonId: item.season
  }
})

const getFormattedParametersOptions = (parametersList, newSeasonId) => {
  const currentOptions = defaultOptions.filter((item) => {
    return parametersList.every((element) => {
      return element.SiteAssesmentParameterTypeId !== item.value || element.SeasonalDistributionId !== item.seasonId
    })
  })

  return newSeasonId ? currentOptions.filter((item) => {
    return item.seasonId === newSeasonId
  }) : defaultParameterOptions.SeasonalDistributionId.filter((item) => {
    return currentOptions.some((element) => {
      return element.seasonId === item.value
    })
  })
}

const SpeciesParameter = ({
  parameter,
  removeOnCLik,
  speciesId,
  getSiteSpecie,
  updateParameterList,
  checkParameterField,
  site,
  species,
  qualified,
  setSiteProposalFieldValidation,
  congregatoryGroup,
  handleChangeCongregatoryGroup,
}) => {
  const params = useParams()
  const { profile } = useContext(ProfileContext)
  const { hasSiteIBAAccessForBirds } = useIBAAccess()

  // Use IBA access control for birds details based on site's country.
  // Individual proposers/co-proposers can see sensitive species when visibility has been approved for this site.
  const isBirdSpecies = species.className === 'AVES'
  const hasIbaAccessForBirds = hasSiteIBAAccessForBirds(site, species.className)
  const hasBirdDetailsOverrideAccess =
    site.SiteStatusId === SITE_STATUS_PROPOSED_ID ||
    site.SiteStatusId === SITE_STATUS_NOMINATED_ID ||
    site.SiteStatusId === SITE_STATUS_REVIEWED_BY_RFP_ID ||
    canViewSensitiveSpeciesAtSiteFullAccess(site, profile.RoleId)

  let showBirdsDetails = isBirdSpecies && (hasIbaAccessForBirds || hasBirdDetailsOverrideAccess)
  
  const availableSeasonsOptions = getFormattedParametersOptions(species.SpeciesAssesmentParameters)
  const isResidentPresent = availableSeasonsOptions.find((item) => {
    return item.value === SEASON_RESIDENT_ID
  })

  const ibaFields = Object.keys(parameter)
  const regionalIbaFieldKeys = [
    'minRegional',
    'bestRegional',
    'maxRegional',
    'sourceRegionalData',
    'sourceOfEu',
    'euPopulation',
    'nationalPopulation',
    'sourceOfNational'
  ]
  if (!regionalIbaFieldKeys.every((key) => ibaFields.includes(key))) {
    showBirdsDetails = false
  }

  const { siteProposalFieldValidation, isEditable } = useContext(SiteContext)

  const disabled = site.SiteStatusId !== SITE_STATUS_DRAFT_ID || (!isEditable && site.SiteStatusId === SITE_STATUS_DRAFT_ID)

  const [parameterId, setParameterId] = useState(
    defaultParameterOptions.parameterId.find((item) => {
      return Number(item.value) === Number(parameter.SiteAssesmentParameterTypeId)
    }) || null
  )

  const [bestGlobal, setBestGlobal] = useState(parameter.bestGlobal || '')
  const [natureOfOccurance, setNatureOfOccurance] = useState(getIdCriteria(parameter, arrIdCriteria).includes(D1b_Value))
  const [justification, setJustification] = useState(!getIdCriteria(parameter, arrIdCriteria).some(num => num === D1b_Value || num === D1a_Value || num === B2_Value || num === D2_Value || num === D3_Value))

  const [season, setSeason] = useState(
    defaultParameterOptions.SeasonalDistributionId.find((item) => {
      return item.value === parameter.SeasonalDistributionId
    }) || isResidentPresent || null)

  function handleChangeSeasons(e) {
    setSeason(e)
  }

  function handleChangeParameter(e) {
    setParameterId(e)
    requestCreateSiteSpecieAssesmentParameter(
      toNumberOrNull(speciesId),
      Number(e.value),
      Number(season.value),
      toNumberOrNull(params.siteHistoryId),
      toNumberOrNull(species.historyId)
    ).then(() => {
      setSiteProposalFieldValidation(defaultSiteProposalValidationResults)
      getSiteSpecie()
    })
  }

  function handleRemoveParameter() {
    if (disabled) {
      return
    }
    removeOnCLik(parameter.id)
  }

  const checkFields = siteProposalFieldValidation.assessmentParameters[parameter.id]
    ? siteProposalFieldValidation.assessmentParameters[parameter.id]
    : defaultSiteProposalValidationResults.assessmentParameters;

  const validationFields = qualified
    ? Object.values(checkFields).filter((element) => {
      return !element;
    })
    : [];


  return (
    <SpeciesAssessmentStyleWrapper>
      <SpeciesDetailsContainer>
        <SpeciesDetailsItemWrapper>
          <Select
            value={season}
            onChange={handleChangeSeasons}
            label='season'
            field='season'
            options={availableSeasonsOptions}
            height='48px'
            marginBottom='24px'
            placeholder='selectSeasonalDistributionAppliedTo'
            infoTooltip='seasonHint'
            zIndex='1070'
            disabled={parameterId || disabled}
            dataTest={`season-${season?.value}`}
          />
        </SpeciesDetailsItemWrapper>
        <SpeciesDetailsParameterIdContainer>
          <SpeciesDetailsParameterIdWrapper>
            <Select
              value={parameterId}
              onChange={handleChangeParameter}
              placeholder='assessmentParameterApplied'
              dataTest={`parameter-${parameterId?.value}`}
              label='parameter'
              field='parameter'
              options={season ? getFormattedParametersOptions(species.SpeciesAssesmentParameters, Number(season.value)) : []}
              height='48px'
              marginBottom='24px'
              infoTooltip='parameterHint'
              zIndex='1070'
              disabled={parameterId || !season || disabled}
              error={
                qualified && !checkParameterField
                  ? {
                    field: 'parameter',
                    error: 'pleaseCompleteThisField'
                  }
                  : null
              }

            />
          </SpeciesDetailsParameterIdWrapper>
          <SpeciesDetailsRemoveParameter onClick={handleRemoveParameter}
            disabled={disabled}>
            <RemoveIcon />
          </SpeciesDetailsRemoveParameter>
        </SpeciesDetailsParameterIdContainer>
        {validationFields.length > 0 && (
          <SpeciesValidationIconWrapper>
            <SpeciesValidationIcon>
              <PulsingNotification number={validationFields.length} />
            </SpeciesValidationIcon>
          </SpeciesValidationIconWrapper>
        )}
        {showBirdsDetails && (Number(parameterId?.value) === NUMBER_OF_MATURE_INDIVIDUALS_ID) && (
          <SpeciesDetailsItemWrapper>
            <Select
              value={congregatoryGroup}
              onChange={handleChangeCongregatoryGroup}
              label='congregatoryGroup'
              field='congregatoryGroup'
              options={congregatoryGroupOptions}
              height='48px'
              marginBottom='24px'
              placeholder='congregatoryGroup'
              zIndex='1070'
              disabled={disabled}
              dataTest={`congregatoryGroup-${congregatoryGroup?.value}`}
            />
          </SpeciesDetailsItemWrapper>
        )}
      </SpeciesDetailsContainer>
      <SpeciesAcordionWrapper>
        <Accordion
          items={[{ name: 'globalData' }]}
          contentComponent={SpeciesParameterGlobalDataContent}
          headerComponent={SpeciesParameterHeader}
          contentProps={{ parameter, siteSpecieParameterId: parameter.id, updateParameterList, species, qualified, assessmentParameter: parameterId || { value: null, label: null }, siteBestGlobal: bestGlobal, setSiteBestGlobal: setBestGlobal, siteNatureOfOccurance: natureOfOccurance, setSiteNatureOfOccurance: setNatureOfOccurance, siteJustification: justification, setSiteJustification: setJustification }}
          initialActive={null}
        />
        {showBirdsDetails && (Number(parameterId?.value) === NUMBER_OF_MATURE_INDIVIDUALS_ID) && (
          <Accordion
            items={[{ name: 'regNatEstimates' }]}
            contentComponent={RegionalNationalEstimatesDataContent}
            headerComponent={SpeciesParameterHeader}
            contentProps={{ parameter, siteSpecieParameterId: parameter.id, updateParameterList, species, qualified, assessmentParameter: parameterId || { value: null, label: null }, siteBestGlobal: bestGlobal, setSiteBestGlobal: setBestGlobal, siteNatureOfOccurance: natureOfOccurance, setSiteNatureOfOccurance: setNatureOfOccurance, siteJustification: justification, setSiteJustification: setJustification }}
            initialActive={null}
          />
        )}
        <Accordion
          items={[{ name: 'siteSpeciesData' }]}
          contentComponent={SpeciesParameterSiteSpeciesDataContent}
          headerComponent={SpeciesParameterHeader}
          contentProps={{ parameter, siteSpecieParameterId: parameter.id, updateParameterList, species, qualified, assessmentParameter: parameterId || { value: null, label: null }, siteBestGlobal: bestGlobal, setSiteBestGlobal: setBestGlobal, siteNatureOfOccurance: natureOfOccurance, setSiteNatureOfOccurance: setNatureOfOccurance, siteJustification: justification, setSiteJustification: setJustification }}
          initialActive={null}
        />
      </SpeciesAcordionWrapper>
    </SpeciesAssessmentStyleWrapper>
  )
}

SpeciesParameter.propTypes = {
  parameter: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    SiteAssesmentParameterTypeId: PropTypes.number,
    SeasonalDistributionId: PropTypes.number,
    bestGlobal: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }).isRequired,
  removeOnCLik: PropTypes.func.isRequired,
  speciesId: PropTypes.number.isRequired,
  getSiteSpecie: PropTypes.func.isRequired,
  updateParameterList: PropTypes.func.isRequired,
  checkParameterField: PropTypes.bool.isRequired,
  site: PropTypes.shape({
    SiteStatusId: PropTypes.number.isRequired
  }).isRequired,
  species: PropTypes.shape({
    SpeciesAssesmentParameters: PropTypes.array.isRequired,
    historyId: PropTypes.number.isRequired,
    className: PropTypes.string
  }).isRequired,
  qualified: PropTypes.bool.isRequired,
  setSiteProposalFieldValidation: PropTypes.func.isRequired,
  congregatoryGroup: PropTypes.oneOfType([
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
      label: PropTypes.string
    }),
    PropTypes.oneOf([null])
  ]),
  handleChangeCongregatoryGroup: PropTypes.func.isRequired
}

export default SpeciesParameter
